/**
 * The Acervo delete gate — pure logic, no React, no gateway.
 *
 * Contract + rulings: `ai-docs/handoffs/acervo-file-delete-feasibility-2026-08-01.md`
 * (§7 shape, §7-bis the confirm ruling, §9 this lane's half). The runtime half
 * is `document.delete` in `extensions/lexiun-core/src/gateway-methods/`.
 *
 * Everything here answers ONE question — *may this row show a delete button?* —
 * and it is deliberately separate from the component so the answer is testable
 * without a DOM. The button is only ever a convenience: the runtime re-checks
 * the role, the `sources/` floor, and the `confirmed` interlock on every call,
 * so a wrong answer here degrades the UX, never the authorization.
 */

/**
 * SHIP GATE — ✅ **`true` since 2026-08-01**, on this lane's probe of the RULED
 * behaviour (details under "THE RULING" below). It reached that state the hard
 * way: live → dark → live, in one day.
 *
 * ── WHAT HAPPENED (HISTORY. Everything in this section describes a state that
 *    NO LONGER HOLDS — the copy it quotes is retired and the const it describes
 *    as `false` is now `true`. The part that governs is "THE RULING" below.) ──
 *
 * ⛔ It went **back to `false` hours after first going live.** Not a rollback of
 * a broken build: the code worked exactly as specified. The SPEC was wrong, and
 * this const is the lever that made that survivable.
 *
 * **`mission-ingest`'s §11 Test A passed 6 of 7 assertions. The 7th was
 * recoverability, which the confirm copy AT THE TIME promised.** The case repo's
 * `.gitignore` allow-lists only `*.md` / `*.json` inside `sources/**` and
 * `raw-intake/**`, so the document BYTES are tracked by nothing. Measured, not
 * inferred: the blob did not exist in the pre-delete checkpoint and had no
 * commits, ever. The checkpoint restores the metadata, the machine extraction
 * and the catalog — **never the evidence.** `removeAtomically` then hard-`rm`s
 * its staging dir. (A recycle bin has since landed — `0a6bf3e7b` — but it is an
 * operator escape hatch, free to purge with no retention promise, and no tool
 * the assistant can call reaches it.)
 *
 * So the confirm as shipped that morning — *"removido do caso — para recuperar,
 * peça ao assistente"* — told a lawyer their file was recoverable when it was
 * not. **We had removed "irreversible" because it lied in the GRAVE direction,
 * and replaced it with a recovery promise that lied in the REASSURING one.**
 * Worst exactly where it matters most: for a `.txt` the extraction keeps the
 * words; for a PDF, image, audio, video or office file the original is
 * destroyed and only a text approximation survives — of the zone the
 * architecture calls the case's immutable audit floor. It also put the feature
 * in conflict with the
 * platform's standing soft-delete-only rule, which this was never ruled an
 * exception to.
 *
 * It went dark rather than waiting live for a ruling: going dark is the cheap,
 * reversible half of a wait, a single click is not reversible at all, and the
 * person most likely to click was the operator who asked for the feature.
 *
 * ── THE RULING (current — this is the part that governs) ──
 *
 * **RULED 2026-08-01, then REVISED the same day — read the revision, not the
 * first ruling.** The recycle bin was approved only because the alternative
 * (tracking blobs in git) would have hit git history and restore; the operator
 * then clarified that reinstating a deleted file **is not a goal at all**
 * (*"we really should not worry about reinstating a deleted file"*).
 *
 * So the copy does not promise recovery and does not deny it: the shipped
 * string is bare **"removido do caso"** (`caseFiles.delete.consequence`). It
 * states the outcome and claims nothing in either direction — which is the only
 * position that survived the day, having been wrong first in the grave
 * direction ("irreversible") and then in the reassuring one ("peça ao
 * assistente"). ⚠ The bin is **free to purge with no retention promise**, so
 * nothing may be built on an entry surviving.
 *
 * ✅ **FLIPPED `true` 2026-08-01 (second time), on the RULED behaviour.** Image
 * `dev-20260801.1549-ad65c54a2`, fleet 8/8, case `cadeiras-ingest-slice` — which
 * is galvão's `m-ingest-slice--slice` on disk, confirmed via `CASE_MODEL.json`
 * rather than assumed from the folder name.
 *
 * The artifact was verified in-container **with a control**, and the control is
 * the whole point: `dist/extensions/lexiun-core` (2.7 MB) carries the symbols
 * with `zzNotARealSymbolZz` at 0 — while `dist-runtime/…/index.js` (382 bytes,
 * a re-export shim) reads **0 for every symbol including ones that certainly
 * exist**. A probe aimed there is indistinguishable from a failed roll.
 *
 * ⚠ **This bundle PRESERVES COMMENTS, so a `grep -c` can FALSE-POSITIVE**
 * (`lexruntime-changes`, 2026-08-01 — a retracted string of theirs appears in it
 * as a comment *describing the retraction*). That is the more dangerous
 * direction: a false absence looks like a failed roll and gets investigated, a
 * false positive looks like confirmation. So these were confirmed by READING the
 * hits, not counting them — `const FILE_RECYCLE_BIN_DIR = ".case-file-recycle-bin"`
 * and `const DELETED_RECEIPT = "DELETED.json"` are live declarations, each with
 * comment hits sitting alongside that a count would have conflated.
 *
 * **None of the flip rests on that grep anyway.** It rests on the RPC answering
 * three different input-dependent refusals and on the dialog rendering the ruled
 * string in a browser — **a comment cannot answer an RPC.** Prefer a behavioural
 * probe to an artifact probe whenever one is available.
 *
 * Then the SPA side: the three refusal probes answered identically against the
 * rolled image (so the method survived the roll), the affordance renders for a
 * `LEAD`, and **the dialog shows the RULED string** — which is the question this
 * flip actually turns on, and the one the first flip never asked.
 *
 * ~~An `lx_*` tool can list and restore a binned source.~~ **STRUCK** — this
 * lane raised it and `mission-ingest` adopted it, then retracted it when the
 * ruling landed: no restore tool is being built. Recorded rather than deleted
 * because the reasoning still holds *conditionally* — **if a recovery clause is
 * ever re-added to this copy, it needs both a new ruling AND an actor who can
 * honour it.** Measured 2026-08-01: nothing the assistant can call reaches a
 * per-file bin (`lx_case_history`/`lx_case_restore` are GIT-based and blobs are
 * ungitted; `.case-recycle-bin` is the whole-CASE bin behind `case.delete`).
 *
 * ✅ Checked and CLEARED while investigating (do not re-derive): `lx_delete`'s
 * near-identical claim is SOUND. The case `.gitignore` is scoped to the two
 * blob zones (`sources/**`, `raw-intake/**`) and everything else is tracked
 * whole, binaries included — and `lx_delete` refuses exactly those two zones.
 * Do not let "the checkpoint restores metadata, never evidence" get
 * generalised into "checkpoints do not protect files": outside those two zones
 * they do.
 *
 * ── The 2026-08-01 live probe that earned the first flip (still valid, and
 *    still not sufficient on its own — it proved the button reaches the method,
 *    which was never the thing in doubt here) ──
 *
 * ⚠ **FLIP THIS ONLY ON THIS LANE'S OWN LIVE PROBE.** Not on a peer's commit
 * SHA, not on a builder's artifact grep. The precedent is
 * `CLIENT_DELETE_BACKEND_READY` (`ClientAttributesDialog.tsx`), which was
 * flipped true on a builder's grep, and the runtime lane's discriminating
 * symbol probe then showed the commit had never made that image — an enabled
 * button against the old handler, which in that case meant data loss rather
 * than an error toast. If a rollback ever reopens the skew, set `false` again
 * and the affordance simply stops rendering.
 *
 * **What earned the flip** (image `dev-20260801.0605-1e1a725de`, case
 * `cadeiras-ingest-slice`, via the SPA's own `window.__rpc` — the same client
 * the button uses, through lexserver, to the runtime):
 *
 * | probe | input | answer |
 * |---|---|---|
 * | path floor | `CASE.md` | `INVALID_PARAMS` "only removes files under sources/ (got: CASE.md)" |
 * | confirm interlock | no `confirmed` | `INVALID_PARAMS` "confirmed:true is required" |
 * | role, then existence | absent `sources/` file | **`NOT_FOUND`** — passed the OWNER gate |
 * | org scope | `scope:"org"` | `INVALID_PARAMS` "case-scoped; the org workspace has no case owner" |
 *
 * **The discriminator is the VARIATION, not the errors.** Four inputs produced
 * four different handler-specific answers at four different guard depths, each
 * quoting a string literal that exists only in `document-delete.ts`. A gateway
 * that did not have the method could not do that — it would return one generic
 * failure regardless of input. That is the "what would be DIFFERENT if the
 * thing were broken" test, and it is why an error-shaped response counts as
 * proof of life here rather than as a failure.
 *
 * `NOT_FOUND` (not `FORBIDDEN`) on the third probe is the runtime's own verdict
 * that the caller passed the OWNER check, and `__cwsStore`'s `data.role` reads
 * `LEAD` — so the SPA gate and the runtime agree. That cross-check is the whole
 * point: had this gated on the SPA's own (then-stale) `"OWNER"` spelling, it
 * would have returned false against a `LEAD` wire value and hidden the button
 * from a genuine case owner, silently and fail-closed.
 *
 * **What that probe did NOT prove — and this is the whole lesson:** that a real
 * delete leaves a recoverable case. No file was deleted; every probe was refused
 * by design. It answered "does the button reach the method", which is a
 * PLUMBING question, and I flipped a user-facing promise on it. The question
 * that mattered — "is the promise in my own copy true" — was in someone else's
 * test plan, and I shipped ahead of it.
 *
 * While `false` the affordance is not rendered AT ALL (rather than rendered
 * disabled): a delete the lawyer cannot explain the absence of is better than a
 * dead control they will click and report.
 */
export const ACERVO_DELETE_BACKEND_READY = true;

/**
 * The ONE role permitted to delete from the Acervo (operator ruling 2026-08-01:
 * "case OWNER only"). Mirrors the runtime's `DELETE_REQUIRED_ROLE`.
 *
 * ⚠ **TWO NAMES, ONE ROLE.** The live `AssignmentRole` enum
 * (`lx-kernel/src/access/flag-projection.ts`) is
 * `LEAD | HANDLER | CONTRIBUTOR | OBSERVER`, and its own docblock says "At case
 * level: LEAD is the case owner". `OWNER` is the PRE-A6 spelling of that same
 * role — lx-kernel's `access/permissions.ts` still carries an explicit legacy
 * shim mapping `OWNER → LEAD`. `case.open` emits the kernel enum, i.e. `LEAD`,
 * verified at `gateway-methods/case.ts` (`role: resolved.role`, typed
 * `AssignmentRole`) rather than assumed from any SPA type.
 *
 * This SPA's `types/access.ts` declared the PRE-A6 union until 2026-08-01, when
 * this feature tripped over it; it now says `LEAD` with the legacy name kept as
 * an input-only alias. The trap is worth remembering even though that file is
 * fixed: a type that is merely STALE reads as current, so trusting the local
 * declaration was the natural move and would have shipped a control that never
 * appears for anyone.
 *
 * Both spellings are accepted here BECAUSE THEY ARE THE SAME ROLE, not as a
 * tolerant fallback: no other role gains anything by it, and the runtime is the
 * enforcer regardless. Matching `LEAD` alone would hide the button from a real
 * owner if any legacy path still spells it `OWNER`; matching both cannot widen
 * the gate past the ruling.
 */
const OWNER_ROLE_NAMES: ReadonlySet<string> = new Set(["LEAD", "OWNER"]);

/**
 * Is the cascade-resolved case role the case OWNER?
 *
 * ABSENT IS NOT AN ANSWER AND MUST NEVER BE FABRICATED INTO ONE. The store's
 * own docblock is explicit: `role` is undefined when the runtime could not
 * resolve a grant, and "NEVER fabricate OBSERVER". An unresolved role therefore
 * hides the affordance exactly as a non-owner does — the two produce the same
 * DECISION while remaining different FACTS, and this function only decides.
 */
export function isCaseOwnerRole(role: string | undefined | null): boolean {
  if (!role) return false;
  return OWNER_ROLE_NAMES.has(role.trim().toUpperCase());
}

/**
 * A `sources/` resource is the ONLY deletable entry — the same floor the
 * runtime's `checkSourcesPath` enforces. Everything the delete guarantees (the
 * atomic member set, the handle retirement, the catalog rebuild, the census's
 * reaction) is a `sources/` contract; offering the button anywhere else would
 * look identical and carry none of it.
 *
 * This MIRRORS the runtime's three rules — `sources/` prefix, at most one
 * subzone deep, and no `_`-prefixed basename (`_index.json` / `_INDEX.md` are
 * the catalog's own DERIVED artifacts, not sources; deleting one is meaningless
 * because the next rebuild re-derives it).
 *
 * ⚠ **The third clause is DEFENCE IN DEPTH, not a hole being plugged** —
 * corrected 2026-08-01 after `mission-ingest` measured the actual guarantee.
 * `isUserFacingFile` (which filters only `.lexmeta.json`) is indeed not it, but
 * the real guard is older and server-side: `buildFilteredTree` applies
 * `isHiddenSegment` — `name.startsWith(".") || name.startsWith("_")` — to every
 * directory entry before it reaches the file branch (`gateway-methods/
 * case-tree.ts`), the same rule as lx-kernel's `isHiddenPath`. So a
 * `_`-prefixed name never becomes a tree node and no row can offer the button.
 * The clause stays because it MIRRORS that rule rather than inventing a second
 * policy, and it survives a future tree change.
 *
 * The same walk hides every machine companion via `isInternalFile`
 * (`.processed.md`, `.info.md`, `.messages.json`, `.lexmeta.json`), which is
 * what makes this gate's core assumption sound rather than lucky: **the row a
 * lawyer can click is ALWAYS the frozen original**, so the runtime resolving the
 * member set from the clicked file's stem is well-founded.
 *
 * ⚠ If the two copies ever diverge, THIS one must stay the NARROWER. It can
 * only hide a button; it can never authorize anything — the runtime re-checks
 * every rule on the call.
 *
 * Kept separate from `isSourceResource` (the copy/move gate in `CaseFileRow`)
 * even though both start at `sources/`: these are two different PERMISSIONS
 * that happen to share a floor, and folding them together is how a later change
 * to one silently moves the other.
 */
export function isDeletableSourcePath(path: string): boolean {
  if (!path.startsWith("sources/")) return false;
  const rest = path.slice("sources/".length);
  if (!rest || rest.endsWith("/")) return false;
  const segments = rest.split("/");
  // `sources/<name>` or `sources/<subzone>/<name>` — no deeper.
  if (segments.length > 2) return false;
  return !segments[segments.length - 1].startsWith("_");
}

export interface DeleteAffordanceInput {
  /** Cascade-resolved case role from `useCaseWorkspaceStore`. */
  role: string | undefined | null;
  /** Workspace-relative path of the row. */
  path: string;
  /** Case scope resolved? Org scope has no case owner, so "owner only" has no
   *  meaning there and the runtime refuses it outright. */
  inCaseScope: boolean;
  /** Defaults to the ship gate; injectable so tests never depend on its value. */
  backendReady?: boolean;
}

/** Should this row render a delete affordance? All four gates must pass. */
export function canDeleteSource({
  role,
  path,
  inCaseScope,
  backendReady = ACERVO_DELETE_BACKEND_READY,
}: DeleteAffordanceInput): boolean {
  return (
    backendReady && inCaseScope && isDeletableSourcePath(path) && isCaseOwnerRole(role)
  );
}

/**
 * Map a `GatewayRpcError.code` onto an i18n key suffix under
 * `caseFiles.delete.errors.*`.
 *
 * The runtime reports every delete failure on the ERROR envelope, which drops
 * all keys but `{code, message}` — so the CODE is the only machine-readable
 * thing that survives, and prose-matching the message is what this exists to
 * avoid. An unrecognised (or absent) code lands on `generic` rather than being
 * shown verbatim: the runtime's messages are English operator prose, and this
 * surface is a lawyer's.
 *
 * `FAILED_PRECONDITION` earns its own line because it is the ONE failure that
 * is not an error in the user's sense — the delete was REFUSED on purpose,
 * because no restore point could be made. Collapsing it into "something went
 * wrong" would hide that the file is still safely there.
 */
export function deleteErrorKey(code: string | undefined): string {
  switch (code) {
    case "FORBIDDEN":
      return "forbidden";
    case "NOT_FOUND":
      return "notFound";
    case "FAILED_PRECONDITION":
      return "noRestorePoint";
    case "INVALID_PARAMS":
      return "invalidPath";
    default:
      return "generic";
  }
}
